/**
 * Penyeragaman tampilan angka pada tabel dasbor.
 *
 * Sebelumnya tiap dasbor mencetak nilai apa adanya, sehingga muncul angka
 * seperti 1638576630.1700002 - sulit dibaca dan tidak mungkin dibandingkan
 * sekilas. Di sini angka diberi pemisah ribuan gaya Indonesia dan dipangkas
 * maksimal dua angka di belakang koma.
 *
 * Kolom angka dirata-kanankan supaya satuannya berbaris; ukuran tabel tetap
 * simetris karena perataan diterapkan pada HEADER dan SEL sekaligus, bukan
 * pada salah satunya saja.
 */

/**
 * Kunci yang kebetulan berisi angka tetapi BUKAN besaran: tahun, bulan,
 * pengenal, dan nomor CAS. Nilai seperti 2026 tidak boleh berubah menjadi
 * "2.026", dan CAS 107-21-1 tidak boleh disentuh sama sekali.
 */
const BUKAN_BESARAN = /(^|_)(id|ids|tahun|year|bulan|month|cas|casnumber|nomor|no|kode|npwp|telp|tahunpembuatan)(_|$)/i;

const PEMFORMAT = new Intl.NumberFormat("id-ID", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** Ubah nilai menjadi angka bila memang angka; selain itu null. */
export function keAngka(nilai) {
  if (typeof nilai === "number") return Number.isFinite(nilai) ? nilai : null;
  if (typeof nilai === "string") {
    const teks = nilai.trim();
    // Hanya terima bentuk angka murni; "12 Kg" atau "107-21-1" dibiarkan.
    if (!/^-?\d+(\.\d+)?$/.test(teks)) return null;
    const angka = Number(teks);
    return Number.isFinite(angka) ? angka : null;
  }
  return null;
}

/** Apakah kolom ini sebuah besaran yang layak diformat dan dirata-kanankan. */
export function kolomBesaran(kunci, nilai) {
  if (kunci && BUKAN_BESARAN.test(String(kunci))) return false;
  return keAngka(nilai) !== null;
}

/**
 * Angka siap tampil: pemisah ribuan, maksimal dua desimal.
 * Nilai yang bukan angka dikembalikan apa adanya.
 */
export function formatAngka(nilai) {
  const angka = keAngka(nilai);
  return angka === null ? nilai : PEMFORMAT.format(angka);
}

/** Isi sel: angka diformat, sisanya apa adanya, kosong menjadi "-". */
export function isiSel(kunci, nilai) {
  if (nilai === null || nilai === undefined || nilai === "") return "-";
  return kolomBesaran(kunci, nilai) ? formatAngka(nilai) : nilai;
}

/**
 * Kelas perataan untuk sel sekaligus header.
 *
 * `tabular-nums` membuat lebar tiap digit sama sehingga kolom angka tidak
 * bergoyang saat nilainya berubah.
 */
export function kelasKolom(kunci, contohNilai) {
  return kolomBesaran(kunci, contohNilai) ? "text-right tabular-nums" : "";
}

/**
 * Menebak perataan sebuah kolom dari SELURUH baris, bukan dari baris pertama
 * saja - baris pertama bisa saja kosong sementara sisanya angka.
 */
export function kelasKolomDariData(kunci, data) {
  const baris = Array.isArray(data) ? data : [];
  const contoh = baris.find((item) => item && item[kunci] !== null && item[kunci] !== undefined && item[kunci] !== "");
  return kelasKolom(kunci, contoh ? contoh[kunci] : undefined);
}

/**
 * Judul kolom dari nama kunci data.
 *
 * Kunci datang dalam dua gaya sekaligus: snake_case (periode_tahun) dan
 * camelCase (casNumber). Tanpa pemisahan, camelCase tercetak menjadi
 * "CASNUMBER" yang sulit dibaca.
 */
export function judulKolom(kunci) {
  return String(kunci ?? "")
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .toUpperCase();
}
